import { Controller } from "@hotwired/stimulus"

/**
 * Support inbox off-canvas sidebar (CONEK-8096).
 *
 * Below lg (1024 px) the inbox sidebar leaves the flow and sits off-screen
 * (`-translate-x-full`); a toggle slides it in over the list with a backdrop.
 * At lg and up the sidebar is static again and this controller stays out of
 * the way (no inert, no transform).
 *
 * Lives on the inbox shell, outside the `ticket-content` frame, so it survives
 * frame swaps (the sidebar is not re-rendered on frame requests, CONEK-4296).
 * Toggles inside the frame connect and disconnect as the frame changes.
 *
 * Closes on: the close button, a backdrop click, Escape, following any link in
 * the panel, crossing to lg, and `turbo:before-cache` / `pagehide` so a Back
 * snapshot never shows it open (CONEK-6282 pattern).
 *
 * Not conek_ui's ui--sidebar (0.3.61): that one opens on the global
 * `sidebar:*` window events the app sidebar also listens to, is tied to
 * Ui::SidebarComponent markup, and has no inert/lg sync or before-cache close.
 */
const HIDDEN_CLASS = "-translate-x-full"
// Only while open, or the shadow of the parked panel bleeds onto the list.
const OPEN_CLASS = "shadow-lg"
// Same unit as Tailwind v4's `lg` (64rem): a px query drifts from the CSS when
// the browser's default font size is not 16px, and inert would then stick on a
// sidebar the CSS already shows in the flow.
const DESKTOP_QUERY = "(min-width: 64rem)"

export default class extends Controller {
  static targets = ["panel", "backdrop", "closeButton", "toggle"]

  connect() {
    this.isOpen = false
    this.opener = null
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY)

    this.onKeydown = (event) => {
      if (event.key === "Escape") this.close()
    }
    this.onPanelClick = (event) => {
      if (event.target.closest("a[href]")) this.hide({ restoreFocus: false })
    }
    this.onBeforeCache = () => this.hide({ restoreFocus: false })
    this.onMediaChange = () => {
      if (this.mediaQuery.matches) this.hide({ restoreFocus: false })
      this.syncInert()
    }

    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("turbo:before-cache", this.onBeforeCache)
    window.addEventListener("pagehide", this.onBeforeCache)
    this.mediaQuery.addEventListener("change", this.onMediaChange)
    if (this.hasPanelTarget)
      this.panelTarget.addEventListener("click", this.onPanelClick)
    this.syncInert()
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("turbo:before-cache", this.onBeforeCache)
    window.removeEventListener("pagehide", this.onBeforeCache)
    this.mediaQuery.removeEventListener("change", this.onMediaChange)
    if (this.hasPanelTarget)
      this.panelTarget.removeEventListener("click", this.onPanelClick)
  }

  // A toggle rendered inside the frame after a swap starts in the right state.
  toggleTargetConnected(toggle) {
    toggle.setAttribute("aria-expanded", String(Boolean(this.isOpen)))
  }

  open(event) {
    if (!this.hasPanelTarget) return
    this.opener = event?.currentTarget ?? null
    this.isOpen = true
    this.panelTarget.classList.remove(HIDDEN_CLASS)
    this.panelTarget.classList.add(OPEN_CLASS)
    if (this.hasBackdropTarget) this.backdropTarget.hidden = false
    this.setExpanded(true)
    this.syncInert()
    if (this.hasCloseButtonTarget) this.closeButtonTarget.focus()
  }

  close() {
    this.hide({ restoreFocus: true })
  }

  // Private

  hide({ restoreFocus }) {
    if (!this.isOpen || !this.hasPanelTarget) return
    this.isOpen = false
    this.panelTarget.classList.add(HIDDEN_CLASS)
    this.panelTarget.classList.remove(OPEN_CLASS)
    if (this.hasBackdropTarget) this.backdropTarget.hidden = true
    this.setExpanded(false)
    this.syncInert()
    if (restoreFocus) this.restoreFocus()
    this.opener = null
  }

  setExpanded(expanded) {
    this.toggleTargets.forEach((toggle) =>
      toggle.setAttribute("aria-expanded", String(expanded)),
    )
  }

  // Off-screen below lg must not take Tab focus; at lg the panel is always live.
  syncInert() {
    if (!this.hasPanelTarget) return
    this.panelTarget.inert = !this.isOpen && !this.mediaQuery.matches
  }

  restoreFocus() {
    const target = this.opener?.isConnected
      ? this.opener
      : this.toggleTargets.find((t) => t.offsetParent !== null)
    target?.focus()
  }
}
